import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import CandleChart, { RANGES } from "./CandleChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Featured mini chart panel — a big BTC (or configurable) candle chart with
 * live tick animation and 10s auto-refresh so the portfolio home feels alive.
 */
export default function FeaturedChart({ symbol = "BTC", label = "BTC · Bitcoin" }) {
  const [range, setRange] = useState("1d");
  const [candles, setCandles] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = (spin) => {
      if (spin) setLoading(true);
      axios
        .get(`${API}/markets/chart/${symbol}?span=${range}`)
        .then(({ data }) => { if (!cancelled) setCandles(data?.candles || []); })
        .catch(() => { if (!cancelled) setCandles([]); })
        .finally(() => { if (!cancelled && spin) setLoading(false); });
    };
    load(true);
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load(false);
    }, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, range]);

  const last = candles?.length ? candles[candles.length - 1] : null;
  const first = candles?.length ? candles[0] : null;
  const price = last?.[4];
  const chg = last && first ? ((last[4] - first[1]) / first[1]) * 100 : null;
  const up = (chg ?? 0) >= 0;

  return (
    <div className="border border-white/10 bg-surface p-6" data-testid="featured-chart">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/45">Live chart · {label}</p>
          <div className="mt-1 flex items-baseline gap-3">
            <p className="font-serif text-2xl font-light tracking-tight text-white">
              {price != null ? `$${Number(price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
            </p>
            {chg != null && (
              <span
                className={`font-tabular text-xs ${up ? "text-[#26A69A]" : "text-[#EF5350]"}`}
              >
                {up ? "▲" : "▼"}{Math.abs(chg).toFixed(2)}%
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1.5">
          {RANGES.slice(0, 5).map((r) => (
            <button
              key={r.k}
              onClick={() => setRange(r.k)}
              className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                range === r.k ? "bg-white/15 text-gold" : "text-white/45 hover:text-gold"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>
      <div className="relative mt-4">
        {loading && !candles && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
          </div>
        )}
        <CandleChart candles={candles} mode="candles" prefix="$" live />
      </div>
    </div>
  );
}
